import React from 'react';
import { C, nx, ny, SLOT_REELS_COUNT_W, SLOT_REELS_COUNT_H, SLOT_REELS_COUNT_BASE_H, SLOT_REELS_COUNT_SPIN_SEQ_H } from '../flowCanvas.constants';

const BORDER_R    = 8;
const HEADER_H    = 28;
const BTN_W       = 28;
const BTN_H       = 24;
const BTN_GAP     = 8;
const TOTAL_BTN_W = 5 * BTN_W + 4 * BTN_GAP; // 172px
const TOGGLE_W    = 32;
const TOGGLE_H    = 18;

// spinTime is stored in seconds by the MsTime (MinSec) field
const formatSpinTime = (secs) => {
  if (secs == null || secs === '') return '–';
  const total = Math.max(0, Number(secs) || 0);
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m > 0) return `${m}:${String(s).padStart(2, '0')}`;
  return `${s}s`;
};

// Spin speed gauge (semi-circle, clock-inspired)
const MIN_SPIN    = 1;
const MAX_SPIN    = 30;
const GAUGE_R     = 56; // radius of the arc

// Static clock hand angles for the decorative clock face at the pivot
const HOUR_ANGLE   = (10 / 12) * 2 * Math.PI - Math.PI / 2; // 10 o'clock
const MINUTE_ANGLE = (2  / 12) * 2 * Math.PI - Math.PI / 2; // 10:10 look

function clampSpin(v) {
  return Math.min(MAX_SPIN, Math.max(MIN_SPIN, Math.round(v)));
}

const SlotReelsCountNode = ({
  step,
  selected,
  onClick,
  centreY,
  overrideX,
  overrideY,
  liveCustomisationData,
  onFlowFieldChange,
}) => {
  const x     = overrideX ?? nx(step);
  const y     = overrideY ?? ny(step, centreY);
  const topY  = y - SLOT_REELS_COUNT_H / 2;
  const leftX = x - SLOT_REELS_COUNT_W / 2;

  const slotSettings = (() => {
    const vals = liveCustomisationData?.setup?.globalSettings?.slotSettings;
    return Array.isArray(vals) ? (vals[0] || {}) : (vals || {});
  })();

  const currentReels = Math.max(1, Math.min(5, Number(slotSettings?.reels) || 3));
  const spinSpeed    = clampSpin(Number(slotSettings?.spinSpeed ?? 10));
  const autoStop     = !!slotSettings?.stopAfterSetTime;
  const manuallyStop = !autoStop;

  // ── Reels buttons ──────────────────────────────────────────────────────
  const btnStartX = leftX + (SLOT_REELS_COUNT_W - TOTAL_BTN_W) / 2;
  const btnY      = topY + HEADER_H + 26; // extra room for "Reels Count" sub-header
  // Divider between reels section and speed section
  const dividerY  = topY + HEADER_H + 26 + BTN_H + 14; // topY + 92

  // ── Spin-speed semi-circle gauge ───────────────────────────────────────
  // Dial is raised so number + buttons can sit clearly below the arc
  const GAUGE_CX = x;
  const GAUGE_CY = topY + SLOT_REELS_COUNT_BASE_H - 68; // topY + 186 — fixed to base height

  const ratio        = (spinSpeed - MIN_SPIN) / (MAX_SPIN - MIN_SPIN);
  // In SVG (y-down): angle π = points left, angle 0 = points right (arc sweeps upward)
  const needleAngle  = Math.PI * (1 - ratio);
  const needleLen    = GAUGE_R - 18;
  const needleX      = GAUGE_CX + needleLen * Math.cos(needleAngle);
  const needleY      = GAUGE_CY - needleLen * Math.sin(needleAngle); // y-flip

  // Active arc — from left endpoint sweeping clockwise (upward) to current position
  // Subtends ≤ 180°, so large-arc-flag is always 0
  const activeEndX   = GAUGE_CX + GAUGE_R * Math.cos(needleAngle);
  const activeEndY   = GAUGE_CY - GAUGE_R * Math.sin(needleAngle);
  const activePath   = ratio <= 0.01
    ? null
    : ratio >= 0.99
      ? `M ${GAUGE_CX - GAUGE_R} ${GAUGE_CY} A ${GAUGE_R} ${GAUGE_R} 0 0 1 ${GAUGE_CX + GAUGE_R} ${GAUGE_CY}`
      : `M ${GAUGE_CX - GAUGE_R} ${GAUGE_CY} A ${GAUGE_R} ${GAUGE_R} 0 0 1 ${activeEndX.toFixed(2)} ${activeEndY.toFixed(2)}`;

  const setSpeedFromPointer = (event) => {
    event.stopPropagation();
    const svg = event.currentTarget.ownerSVGElement;
    if (!svg) return;
    const rect   = svg.getBoundingClientRect();
    const dx     = event.clientX - rect.left - GAUGE_CX;
    const dy     = GAUGE_CY - (event.clientY - rect.top); // flip y for top-arc math
    const angle  = Math.atan2(dy, dx);
    const clamped = Math.max(0, Math.min(Math.PI, angle));
    const newRatio = 1 - clamped / Math.PI;
    onFlowFieldChange?.('setup', 'globalSettings', 'slotSettings', 0, 'spinSpeed',
      clampSpin(MIN_SPIN + newRatio * (MAX_SPIN - MIN_SPIN)));
    onClick?.();
  };

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>

      {/* ── Card background ──────────────────────────────────────────── */}
      <rect
        x={leftX} y={topY}
        width={SLOT_REELS_COUNT_W} height={SLOT_REELS_COUNT_H}
        rx={BORDER_R}
        fill={selected ? C.cardSel : C.card}
        stroke={selected ? C.sel : C.border}
        strokeWidth={selected ? 2 : 1.5}
      />

      {/* ── Header band (flat bottom via clip) ───────────────────────── */}
      <clipPath id={`src-hdr-${step.stepId}`}>
        <rect x={leftX} y={topY} width={SLOT_REELS_COUNT_W} height={HEADER_H} />
      </clipPath>
      <rect
        x={leftX} y={topY}
        width={SLOT_REELS_COUNT_W} height={HEADER_H + BORDER_R}
        rx={BORDER_R} ry={BORDER_R}
        clipPath={`url(#src-hdr-${step.stepId})`}
        fill="rgba(145,70,255,0.20)"
      />
      <text
        x={leftX + 12} y={topY + HEADER_H / 2 + 5}
        fontSize="11" fontWeight="700" fill={C.text}
        style={{ userSelect: 'none', pointerEvents: 'none' }}
      >Reels Setup</text>

      {/* ── "Reels Count" sub-header above buttons ───────────────────── */}
      <text
        x={x} y={topY + HEADER_H + 16}
        textAnchor="middle" fontSize="8.5" fontWeight="700" fill={C.textMuted}
        style={{ userSelect: 'none', pointerEvents: 'none', letterSpacing: '0.8px', textTransform: 'uppercase' }}
      >REELS COUNT</text>

      {/* ── 1–5 reel selector buttons ────────────────────────────────── */}
      {[1, 2, 3, 4, 5].map((n, i) => {
        const bx       = btnStartX + i * (BTN_W + BTN_GAP);
        const isActive = n === currentReels;
        return (
          <g
            key={n}
            onClick={(e) => {
              e.stopPropagation();
              onFlowFieldChange?.('setup', 'globalSettings', 'slotSettings', 0, 'reels', n);
              onClick?.();
            }}
            style={{ cursor: 'pointer' }}
          >
            <rect
              x={bx} y={btnY}
              width={BTN_W} height={BTN_H}
              rx="6"
              fill={isActive ? 'rgba(145,70,255,0.75)' : 'rgba(255,255,255,0.05)'}
              stroke={isActive ? C.purple : C.border}
              strokeWidth={isActive ? 1.5 : 1}
            />
            <text
              x={bx + BTN_W / 2} y={btnY + BTN_H / 2 + 1}
              textAnchor="middle" dominantBaseline="middle"
              fontSize="12" fontWeight={isActive ? '700' : '500'}
              fill={isActive ? '#fff' : C.textMuted}
              style={{ userSelect: 'none', pointerEvents: 'none' }}
            >{n}</text>
          </g>
        );
      })}

      {/* ── Divider ──────────────────────────────────────────────────── */}
      <line
        x1={leftX + 1} y1={dividerY}
        x2={leftX + SLOT_REELS_COUNT_W - 1} y2={dividerY}
        stroke={C.border} strokeWidth="0.8"
      />

      {/* ── "Spin Speed" section label (clock icon + text) ───────────── */}
      {/* Tiny inline clock icon */}
      <circle
        cx={leftX + SLOT_REELS_COUNT_W / 2 - 26} cy={dividerY + 11}
        r={5} fill="none" stroke={C.textMuted} strokeWidth={1}
      />
      {/* Hour hand (pointing to ~12) */}
      <line
        x1={leftX + SLOT_REELS_COUNT_W / 2 - 26} y1={dividerY + 11}
        x2={leftX + SLOT_REELS_COUNT_W / 2 - 26} y2={dividerY + 7}
        stroke={C.textMuted} strokeWidth={1} strokeLinecap="round"
      />
      {/* Minute hand (pointing to ~3) */}
      <line
        x1={leftX + SLOT_REELS_COUNT_W / 2 - 26} y1={dividerY + 11}
        x2={leftX + SLOT_REELS_COUNT_W / 2 - 21} y2={dividerY + 11}
        stroke={C.textMuted} strokeWidth={1} strokeLinecap="round"
      />
      <text
        x={leftX + SLOT_REELS_COUNT_W / 2 - 18} y={dividerY + 15}
        fontSize="9" fill={C.textMuted}
        style={{ userSelect: 'none', pointerEvents: 'none' }}
      >Spin Speed</text>

      {/* ── Semi-circle speed gauge (clock-style) ────────────────────── */}
      <g onMouseDown={setSpeedFromPointer} style={{ cursor: 'crosshair' }}>

        {/* Track arc (full semicircle, dim) */}
        <path
          d={`M ${GAUGE_CX - GAUGE_R} ${GAUGE_CY} A ${GAUGE_R} ${GAUGE_R} 0 0 1 ${GAUGE_CX + GAUGE_R} ${GAUGE_CY}`}
          fill="none"
          stroke="rgba(145,70,255,0.15)"
          strokeWidth={7}
          strokeLinecap="round"
        />

        {/* Active arc (left → current, lit) */}
        {activePath && (
          <path
            d={activePath}
            fill="none"
            stroke="rgba(145,70,255,0.60)"
            strokeWidth={7}
            strokeLinecap="round"
          />
        )}

        {/* Clock-style tick marks (13 ticks = half clock face, every 15°) */}
        {Array.from({ length: 13 }, (_, i) => {
          const θ       = Math.PI * (1 - i / 12); // π → 0, left to right
          const isMajor = i % 3 === 0;
          const inner   = GAUGE_R - (isMajor ? 11 : 7);
          return (
            <line
              key={i}
              x1={GAUGE_CX + inner * Math.cos(θ)}
              y1={GAUGE_CY - inner * Math.sin(θ)}
              x2={GAUGE_CX + (GAUGE_R - 2) * Math.cos(θ)}
              y2={GAUGE_CY - (GAUGE_R - 2) * Math.sin(θ)}
              stroke={isMajor ? C.purple : 'rgba(145,70,255,0.35)'}
              strokeWidth={isMajor ? 1.5 : 0.8}
            />
          );
        })}

        {/* Speed needle */}
        <line
          x1={GAUGE_CX} y1={GAUGE_CY}
          x2={needleX} y2={needleY}
          stroke={C.purple} strokeWidth={2.5} strokeLinecap="round"
        />

        {/* Clock face at pivot — decorative circle with hour marks */}
        <circle cx={GAUGE_CX} cy={GAUGE_CY} r={11} fill="rgba(145,70,255,0.12)" stroke={C.purple} strokeWidth={1.5} />
        {Array.from({ length: 12 }, (_, h) => {
          const ha  = (h / 12) * 2 * Math.PI - Math.PI / 2; // 12 o'clock = top
          const inR = 8;
          const ouR = 10;
          return (
            <line
              key={h}
              x1={GAUGE_CX + inR * Math.cos(ha)} y1={GAUGE_CY + inR * Math.sin(ha)}
              x2={GAUGE_CX + ouR * Math.cos(ha)} y2={GAUGE_CY + ouR * Math.sin(ha)}
              stroke={h % 3 === 0 ? C.purple : 'rgba(145,70,255,0.4)'}
              strokeWidth={h % 3 === 0 ? 1.2 : 0.6}
            />
          );
        })}
        {/* Static clock hour hand (~10 o'clock) */}
        <line
          x1={GAUGE_CX} y1={GAUGE_CY}
          x2={GAUGE_CX + 6 * Math.cos(HOUR_ANGLE)}
          y2={GAUGE_CY + 6 * Math.sin(HOUR_ANGLE)}
          stroke={C.text} strokeWidth={1.5} strokeLinecap="round"
        />
        {/* Static clock minute hand (~2 o'clock, 10:10 look) */}
        <line
          x1={GAUGE_CX} y1={GAUGE_CY}
          x2={GAUGE_CX + 8.5 * Math.cos(MINUTE_ANGLE)}
          y2={GAUGE_CY + 8.5 * Math.sin(MINUTE_ANGLE)}
          stroke={C.text} strokeWidth={1} strokeLinecap="round"
        />
        {/* Center pivot dot */}
        <circle cx={GAUGE_CX} cy={GAUGE_CY} r={3.5} fill={C.purple} />
      </g>

      {/* Slow / Fast endpoint labels — flank the arc base */}
      <text
        x={GAUGE_CX - GAUGE_R + 3} y={GAUGE_CY + 10}
        fontSize="7" fontWeight="600" fill={C.textDim}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >Slow</text>
      <text
        x={GAUGE_CX + GAUGE_R - 3} y={GAUGE_CY + 10}
        textAnchor="end" fontSize="7" fontWeight="600" fill={C.textDim}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >Fast</text>

      {/* Speed value — sits clearly below the full dial, above the +/- buttons */}
      <text
          x={GAUGE_CX} y={GAUGE_CY + 48}
          textAnchor="middle" fontSize="18" fontWeight="800" fill={C.text}
        style={{ pointerEvents: 'none', userSelect: 'none' }}
      >{spinSpeed}</text>

      {/* ── +/- step buttons ─────────────────────────────────────────── */}
      <g
        onClick={(e) => {
          e.stopPropagation();
          onFlowFieldChange?.('setup', 'globalSettings', 'slotSettings', 0, 'spinSpeed', clampSpin(spinSpeed - 1));
          onClick?.();
        }}
        style={{ cursor: 'pointer' }}
      >
        <circle cx={leftX + 22} cy={GAUGE_CY + 44} r={10} fill="rgba(255,255,255,0.06)" stroke="rgba(145,70,255,0.30)" strokeWidth={1} />
        <text x={leftX + 22} y={GAUGE_CY + 48} textAnchor="middle" fontSize="16" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>-</text>
      </g>
      <g
        onClick={(e) => {
          e.stopPropagation();
          onFlowFieldChange?.('setup', 'globalSettings', 'slotSettings', 0, 'spinSpeed', clampSpin(spinSpeed + 1));
          onClick?.();
        }}
        style={{ cursor: 'pointer' }}
      >
        <circle cx={leftX + SLOT_REELS_COUNT_W - 22} cy={GAUGE_CY + 44} r={10} fill="rgba(255,255,255,0.06)" stroke="rgba(145,70,255,0.30)" strokeWidth={1} />
        <text x={leftX + SLOT_REELS_COUNT_W - 22} y={GAUGE_CY + 48} textAnchor="middle" fontSize="16" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
      </g>

      {/* ── Spin Sequence section ──────────────────────────────────────── */}
      {(() => {
        const ssy = topY + SLOT_REELS_COUNT_BASE_H + 10; // section start Y
        return (
          <g>
            {/* Divider */}
            <line x1={leftX + 1} y1={ssy} x2={leftX + SLOT_REELS_COUNT_W - 1} y2={ssy} stroke={C.border} strokeWidth="0.8" />

            {/* Section header */}
            <text
              x={leftX + 12} y={ssy + 14}
              fontSize="8.5" fontWeight="700" fill={C.textMuted}
              style={{ userSelect: 'none', pointerEvents: 'none', letterSpacing: '0.8px' }}
            >SPIN SEQUENCE</text>

            {/* "Manually Stop Reels" label */}
            <text
              x={leftX + 12} y={ssy + 37}
              fontSize="10" fill={C.text}
              style={{ userSelect: 'none', pointerEvents: 'none' }}
            >Manually Stop Reels</text>

            {/* Toggle */}
            <g
              onClick={(e) => {
                e.stopPropagation();
                onFlowFieldChange?.('setup', 'globalSettings', 'slotSettings', 0, 'stopAfterSetTime', !autoStop);
                onClick?.();
              }}
              style={{ cursor: 'pointer' }}
            >
              <rect
                x={leftX + SLOT_REELS_COUNT_W - TOGGLE_W - 12}
                y={ssy + 26}
                width={TOGGLE_W}
                height={TOGGLE_H}
                rx={TOGGLE_H / 2}
                fill={manuallyStop ? 'rgba(145,70,255,0.70)' : C.circle}
                stroke={manuallyStop ? 'rgba(145,70,255,0.9)' : C.border}
                strokeWidth="1"
              />
              <circle
                cx={leftX + SLOT_REELS_COUNT_W - TOGGLE_W - 12 + (manuallyStop ? TOGGLE_W - TOGGLE_H / 2 : TOGGLE_H / 2)}
                cy={ssy + 35}
                r="7"
                fill={manuallyStop ? '#fff' : C.border}
              />
            </g>

            {/* Spin Time row — only shown when auto-stop is ON (manuallyStop = false) */}
            {autoStop && (
              <g>
                <line x1={leftX + 1} y1={ssy + 50} x2={leftX + SLOT_REELS_COUNT_W - 1} y2={ssy + 50} stroke={C.border} strokeWidth="0.5" opacity="0.4" />
                <text
                  x={leftX + SLOT_REELS_COUNT_W / 2} y={ssy + 62}
                  textAnchor="middle" fontSize="8.5" fontWeight="700" fill={C.textMuted}
                  style={{ userSelect: 'none', pointerEvents: 'none', letterSpacing: '0.8px' }}
                >SPIN TIME</text>
                {/* Minus button */}
                <g
                  onClick={(e) => {
                    e.stopPropagation();
                    const v = Math.max(1, (Number(slotSettings?.spinTime) || 5) - 1);
                    onFlowFieldChange?.('setup', 'globalSettings', 'slotSettings', 0, 'spinTime', v);
                    onClick?.();
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <circle cx={leftX + 22} cy={ssy + 76} r={9} fill="rgba(255,255,255,0.06)" stroke="rgba(145,70,255,0.30)" strokeWidth={1} />
                  <text x={leftX + 22} y={ssy + 80} textAnchor="middle" fontSize="16" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>-</text>
                </g>
                {/* Value */}
                <text
                  x={leftX + SLOT_REELS_COUNT_W / 2} y={ssy + 81}
                  textAnchor="middle" fontSize="14" fontWeight="800" fill={C.text}
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >{formatSpinTime(slotSettings?.spinTime)}</text>
                {/* Plus button */}
                <g
                  onClick={(e) => {
                    e.stopPropagation();
                    const v = Math.min(599, (Number(slotSettings?.spinTime) || 5) + 1);
                    onFlowFieldChange?.('setup', 'globalSettings', 'slotSettings', 0, 'spinTime', v);
                    onClick?.();
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <circle cx={leftX + SLOT_REELS_COUNT_W - 22} cy={ssy + 76} r={9} fill="rgba(255,255,255,0.06)" stroke="rgba(145,70,255,0.30)" strokeWidth={1} />
                  <text x={leftX + SLOT_REELS_COUNT_W - 22} y={ssy + 80} textAnchor="middle" fontSize="16" fontWeight="700" fill={C.text} style={{ pointerEvents: 'none', userSelect: 'none' }}>+</text>
                </g>
              </g>
            )}
          </g>
        );
      })()}

    </g>
  );
};

export default SlotReelsCountNode;
